import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useParams, useNavigate } from "react-router-dom";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import API_URL from "../../api/API";

export const AdminEditStaticSpeedWorkout = () => {
  const { staff } = useStaffAuthContext();
  const { workout_id, theme_id } = useParams();
  const navigate = useNavigate();

  const [week, setWeek] = useState("");
  const [days, setDays] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false); // For the confirmation popup
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchWorkoutData = async () => {
      try {
        const response = await fetch(`${API_URL.GET_STATIC_WORKOUT_BY_THEME}/${workout_id}/${theme_id}`, {
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
  
        if (response.ok) {
          const data = await response.json();
          setWeek(data.week); // Week is displayed as a label
          setDays(data.days); // Includes sprints within themes
        } else {
          alert("Failed to load workout data.");
        }
      } catch (error) {
        console.error("Error fetching workout data:", error);
        alert("Failed to fetch workout data.");
      }
    };
  
    fetchWorkoutData();
  }, [workout_id, theme_id, staff.token]);

  const handleAddSprint = (dayIndex, themeIndex) => {
    const updatedDays = [...days];
    updatedDays[dayIndex].themes[themeIndex].sprints.push({
      sets: 0,
      distance: 0,
      reps: 0,
      rest: "",
      notes: "",
      video_url: "",
    });
    setDays(updatedDays);
  };

  const handleDeleteSprint = (dayIndex, themeIndex, sprintIndex) => {
    const updatedDays = [...days];
    updatedDays[dayIndex].themes[themeIndex].sprints = updatedDays[dayIndex].themes[themeIndex].sprints.filter(
      (_, index) => index !== sprintIndex
    );
    setDays(updatedDays);
  };

  const handleInputChange = (e, field, dayIndex, themeIndex, sprintIndex) => {
    const updatedDays = [...days];
    if (sprintIndex !== undefined) {
      updatedDays[dayIndex].themes[themeIndex].sprints[sprintIndex][field] = e.target.value;
    } else if (themeIndex !== undefined) {
      updatedDays[dayIndex].themes[themeIndex][field] = e.target.value;
    } else {
      updatedDays[dayIndex][field] = e.target.value;
    }
    setDays(updatedDays);
  };

  const handleSave = async () => {
    const data = { days };

    try {
      const response = await fetch(`${API_URL.POST_SAVE_STATIC_WORKOUT_BY_THEME}/${workout_id}/${theme_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        alert("Static workout saved successfully!");
      } else {
        const error = await response.json();
        alert(`Failed to save workout: ${error.error}`);
      }
    } catch (error) {
      console.error("Error saving workout:", error);
      alert("Failed to save workout.");
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />
      <div className="flex-grow p-6">
        <h1 className="text-2xl font-bold mb-4">Edit Static Speed Workout</h1>
        <div className="relative">
              {/* Go Back Button */}
              <button
                onClick={() => navigate(-1)}
                className="absolute top-0 right-0 bg-red-500 text-white py-2 px-4 rounded"
              >
                Go Back
              </button>

              <div className="p-6 bg-gray-50 shadow-sm rounded">
                <div className="mb-6">
                  <label className="block text-lg font-semibold mb-1">Week</label>
                  <p className="bg-gray-100 p-2 w-full rounded">{week}</p>
                </div>

                {days.map((day, dayIndex) => (
                  <div key={dayIndex} className="mb-8">
                    <label className="block text-lg font-semibold mb-1">Day Name</label>
                    <p className="bg-gray-100 p-2 w-full font-bold rounded">{day.name}</p>

                    {day.themes.map((theme, themeIndex) => {
                      return (
                        <div key={themeIndex} className="p-4 bg-white rounded-lg shadow mb-4">
                          <div className="mb-4">
                            <label className="block text-lg font-semibold mb-1">Theme Title</label>
                            <input
                              type="text"
                              className="border p-2 w-full rounded"
                              placeholder="Theme Title"
                              value={theme.title}
                              onChange={(e) => handleInputChange(e, "title", dayIndex, themeIndex)}
                            />
                          </div>

                          {theme.sprints.map((sprint, sprintIndex) => (
                            <div key={sprintIndex} className="mb-6">
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-lg font-semibold mb-1">Sets</label>
                                  <input
                                    type="number"
                                    className="border p-2 w-full rounded"
                                    value={sprint.sets}
                                    onChange={(e) => handleInputChange(e, "sets", dayIndex, themeIndex, sprintIndex)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-lg font-semibold mb-1">Reps</label>
                                  <input
                                    type="number"
                                    className="border p-2 w-full rounded"
                                    value={sprint.reps}
                                    onChange={(e) => handleInputChange(e, "reps", dayIndex, themeIndex, sprintIndex)}
                                  />
                                </div>

                                <div>
                                  <label className="block text-lg font-semibold mb-1">Distance</label>
                                  <input
                                    type="number"
                                    className="border p-2 w-full rounded"
                                    value={sprint.distance}
                                    onChange={(e) => handleInputChange(e, "distance", dayIndex, themeIndex, sprintIndex)}
                                  />
                                </div>
                              </div>

                              <div className="mt-4">
                                <label className="block text-lg font-semibold mb-1">Rest</label>
                                <textarea
                                  className="border p-2 w-full rounded"
                                  value={sprint.rest}
                                  onChange={(e) => handleInputChange(e, "rest", dayIndex, themeIndex, sprintIndex)}
                                ></textarea>
                              </div>

                              <div className="mt-4">
                                <label className="block text-lg font-semibold mb-1">Notes</label>
                                <textarea
                                  className="border p-2 w-full rounded"
                                  value={sprint.notes}
                                  onChange={(e) => handleInputChange(e, "notes", dayIndex, themeIndex, sprintIndex)}
                                ></textarea>
                              </div>

                              <div className="mt-4">
                                <label className="block text-lg font-semibold mb-1">Video URL</label>
                                <input
                                  type="text"
                                  className="border p-2 w-full rounded"
                                  value={sprint.video_url}
                                  onChange={(e) => handleInputChange(e, "video_url", dayIndex, themeIndex, sprintIndex)}
                                />
                              </div>

                              <div className="flex justify-end gap-4 mt-4">
                                <button
                                  onClick={() => handleDeleteSprint(dayIndex, themeIndex, sprintIndex)}
                                  className="bg-red-500 text-white py-2 px-4 rounded"
                                >
                                  Delete Sprint
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Add Sprint button moved here */}
                          <div className="flex justify-end mt-4">
                            <button
                              onClick={() => handleAddSprint(dayIndex, themeIndex)}
                              className="bg-blue-500 text-white py-2 px-4 rounded"
                            >
                              Add Sprint
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}

                <div className="flex justify-center mt-6">
                  <button onClick={handleSave} className="bg-blue-500 text-white py-2 px-6 rounded">
                    Save
                  </button>
                </div>
              </div>
            </div>
      </div>
    </div>
  );
};
